// Catálogo de categorías (jerárquico). Inventario → Catálogo → Categorías.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgePercent, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, Categoria, LETRAS_DESCUENTO, Modal, Switch, fieldCls, inv, toNum } from './shared';

interface CategoriaDescuento { id: string; categoria_id: string; letra: string; descuento_pct: number }

interface Nodo extends Categoria { nivel: number }

// Ordena las categorías como árbol (padres seguidos de sus hijos) con nivel
function aplanar(cats: Categoria[]): Nodo[] {
  const hijos = new Map<string | null, Categoria[]>();
  for (const c of cats) {
    const k = c.parent_id ?? null;
    if (!hijos.has(k)) hijos.set(k, []);
    hijos.get(k)!.push(c);
  }
  const out: Nodo[] = [];
  const walk = (parent: string | null, nivel: number) => {
    for (const c of (hijos.get(parent) || []).sort((a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre))) {
      out.push({ ...c, nivel });
      walk(c.id, nivel + 1);
    }
  };
  walk(null, 0);
  return out;
}

const vacio = { parent_id: '', codigo: '', nombre: '', descripcion: '', orden: 0, es_activo: true };

export function Categorias() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: cats = [], isFetching } = useQuery<Categoria[]>({
    queryKey: ['inv-categorias'], queryFn: () => inv.list<Categoria>('categorias', '?select=*'),
  });
  const arbol = useMemo(() => aplanar(cats), [cats]);

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const [descCat, setDescCat] = useState<Categoria | null>(null);

  const abrir = (c?: Categoria) => {
    if (c) {
      setEditId(c.id);
      setForm({ parent_id: c.parent_id ?? '', codigo: c.codigo ?? '', nombre: c.nombre, descripcion: c.descripcion ?? '', orden: c.orden, es_activo: c.es_activo });
    } else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: async () => {
      const body = {
        parent_id: form.parent_id || null, codigo: form.codigo || null, nombre: form.nombre,
        descripcion: form.descripcion || null, orden: Number(form.orden) || 0, es_activo: form.es_activo,
      };
      return editId ? inv.update('categorias', editId, body) : inv.create('categorias', body);
    },
    onSuccess: () => { toast.success('Categoría guardada'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['inv-categorias'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => inv.remove('categorias', id),
    onSuccess: () => { toast.success('Categoría eliminada'); void qc.invalidateQueries({ queryKey: ['inv-categorias'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Categorías</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nueva categoría</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Árbol de categorías ({cats.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Código</th>
              <th className="py-2 pr-3 text-right">Orden</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {arbol.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin categorías</td></tr>}
              {arbol.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3"><span style={{ paddingLeft: c.nivel * 18 }}>{c.nivel > 0 && '└ '}{c.nombre}</span></td>
                  <td className="py-2 pr-3 text-muted-foreground">{c.codigo || '—'}</td>
                  <td className="py-2 pr-3 text-right">{c.orden}</td>
                  <td className="py-2 pr-3">{c.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Descuentos de la familia" onClick={() => setDescCat(c)}><BadgePercent className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted ml-1" title="Editar" onClick={() => abrir(c)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar"
                      onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${c.nombre}"?`, { peligro: true })) eliminar.mutate(c.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {descCat && <DescuentosFamiliaModal categoria={descCat} onClose={() => setDescCat(null)} />}

      {abierto && (
        <Modal title={editId ? 'Editar categoría' : 'Nueva categoría'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Categoría padre">
              <select className={fieldCls} value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })}>
                <option value="">— Raíz —</option>
                {cats.filter((c) => c.id !== editId).map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Código"><input className={fieldCls} value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} /></Campo>
              <Campo label="Orden"><input type="number" className={fieldCls} value={form.orden} onChange={(e) => setForm({ ...form, orden: Number(e.target.value) })} /></Campo>
            </div>
            <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
            <Campo label="Descripción"><textarea className={`${fieldCls} h-20`} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></Campo>
            <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Descuento base por letra para toda la familia (inventario.categoria_descuentos).
// Aplica según la letra de la bodega; contra el descuento del producto gana el mayor.
function DescuentosFamiliaModal({ categoria, onClose }: { categoria: Categoria; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: filas = [] } = useQuery<CategoriaDescuento[]>({
    queryKey: ['inv-cat-descuentos', categoria.id],
    queryFn: () => inv.list<CategoriaDescuento>('categoria_descuentos', `?select=id,categoria_id,letra,descuento_pct&categoria_id=eq.${categoria.id}&deleted_at=is.null`),
    staleTime: 0,
  });
  const [edit, setEdit] = useState<Record<string, string>>({});

  const guardar = useMutation({
    mutationFn: async () => {
      for (const letra of LETRAS_DESCUENTO) {
        const val = edit[letra];
        if (val === undefined) continue;                       // sin cambios
        const existente = filas.find((f) => f.letra === letra);
        const pct = toNum(val);
        if (existente && (val === '' || pct === 0)) await inv.remove('categoria_descuentos', existente.id);
        else if (existente && pct > 0) await inv.update('categoria_descuentos', existente.id, { descuento_pct: pct });
        else if (!existente && pct > 0) await inv.create('categoria_descuentos', { categoria_id: categoria.id, letra, descuento_pct: pct });
      }
    },
    onSuccess: () => {
      toast.success('Descuentos de la familia guardados');
      void qc.invalidateQueries({ queryKey: ['inv-cat-descuentos', categoria.id] });
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title={`Descuentos por familia · ${categoria.nombre}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">
          % base por letra: aplica a todos los productos de la familia en las bodegas que tengan esa letra.
          Si el producto tiene descuento propio, gana el mayor de los dos. Vacío o 0 = sin descuento.
        </p>
        <table className="w-full max-w-sm text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pr-3">Letra</th><th className="py-2 pr-3 text-right">Descuento %</th>
          </tr></thead>
          <tbody>
            {LETRAS_DESCUENTO.map((letra) => {
              const existente = filas.find((f) => f.letra === letra);
              const val = edit[letra] ?? (existente ? String(Number(existente.descuento_pct)) : '');
              return (
                <tr key={letra} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-semibold">{letra}</td>
                  <td className="py-2 pr-3 text-right">
                    <input type="number" min="0" max="100" step="0.01" placeholder="—"
                      className={`${fieldCls} h-9 w-28 text-right ml-auto`} value={val}
                      onChange={(e) => setEdit((s) => ({ ...s, [letra]: e.target.value }))} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Guardar</Button>
        </div>
      </div>
    </Modal>
  );
}
